import { useMemo } from "react";
import { AxiosError } from "axios";
import { useMutation } from "react-query";
import { v4 } from "uuid";

import activities from "../../../redux/activities";
import { AddFieldModifierParams, BasicField } from "../../../models/fields";
import { MutationEnvelope, ensureMutationState } from "../../../services/query";
import { getAuthToken } from "../../../services/granularAxios";
import fieldsService from "../../../services/fields";
import { ensureValue } from "../../../services/util";

import { useMaybeUserId } from "../userId";
import { useMaybeCurrentOperationId } from "../operations";
import { useInvalidateFarms } from "../farms/farms.invalidate";

import { useInvalidateFields } from "./fields.invalidate";
import { useSetFieldsGlobalActivityAndStatus } from "./fields.globalStatus";
import { useMaybeFabricFeatureFlag } from "../featureFlags";

// ####################
// ## useDeleteField ##
// ####################

export type ResultOfUseDeleteField = MutationEnvelope<string, null>;
export type UseDeleteField = () => ResultOfUseDeleteField;

/**
 * Use the `mutate` function returned from this hook to delete the field.
 * This will trigger an invalidation of the farms and fields data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useDeleteField: UseDeleteField = () => {
  const invalidateFields = useInvalidateFields();
  const invalidateFarms = useInvalidateFarms();
  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = Boolean(
    useMaybeFabricFeatureFlag({
      key: "fields-api",
    }),
  );

  const setFieldsGlobalActivityAndStatus =
    useSetFieldsGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    string
  >(
    (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      return fieldsService.removeField(
        {
          authToken: getAuthToken(),
          userId,
          operationId,
          fieldId: payload,
        },
        catalogFlag,
      );
    },
    {
      onMutate: () => {
        setFieldsGlobalActivityAndStatus({
          activity: activities.removeField,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await Promise.all([
          invalidateFields(),
          // invalidate farms as well since they contain the count of fields
          invalidateFarms(),
        ]);

        setFieldsGlobalActivityAndStatus({
          activity: activities.removeField,
          status: "success",
        });
      },
      onError: () => {
        setFieldsGlobalActivityAndStatus({
          activity: activities.removeField,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// ####################
// ## useCreateField ##
// ####################

export type NewField = AddFieldModifierParams;
export type ResultOfUseCreateField = MutationEnvelope<NewField, BasicField>;
export type UseCreateField = () => ResultOfUseCreateField;

/**
 * Use the `mutate` function returned from this hook to create a new field.
 * This will trigger an invalidation of the farms and fields data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useCreateField: UseCreateField = () => {
  const invalidateFields = useInvalidateFields();
  const invalidateFarms = useInvalidateFarms();
  const maybeUserId = useMaybeUserId();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = Boolean(
    useMaybeFabricFeatureFlag({
      key: "fields-api",
    }),
  );

  const setFieldsGlobalActivityAndStatus =
    useSetFieldsGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    BasicField,
    AxiosError,
    NewField
  >(
    (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const operationId = ensureValue(maybeOperationId, "operationId");

      return fieldsService.addField(
        {
          authToken: getAuthToken(),
          userId,
          operationId,
          field: { ...payload, id: v4() },
        },
        catalogFlag,
      );
    },
    {
      onMutate: () => {
        setFieldsGlobalActivityAndStatus({
          activity: activities.addField,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await Promise.all([
          invalidateFields(),
          // invalidate farms as well since they contain the count of fields
          invalidateFarms(),
        ]);

        setFieldsGlobalActivityAndStatus({
          activity: activities.addField,
          status: "success",
        });
      },
      onError: () => {
        setFieldsGlobalActivityAndStatus({
          activity: activities.addField,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};
